import React, { useCallback } from 'react';
import BeaconComparableSplineChart from 'src/components/BeaconV2Components/BeaconMetricSplineChart/BeaconComparableSplineChart';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import { PromotionMetricCards } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/PromotionMetricCards';
import { PromotionCountDonutChart } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/Visualizations/PromotionCountDonutChart';
import { PromotionsByWeekTable } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/Visualizations/PromotionsByWeekTable';
import PromotionsDealTypeColumnChart from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/Visualizations/PromotionsDealTypeColumnChart';
import { PromotionsProductTable } from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/Visualizations/PromotionsProductTable';
import PromotionsTopEntityChart from 'src/components/BeaconV2Components/ExperimentalLayout/Prices/Promotions/Visualizations/PromotionsTopEntityChart';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { CommonSummaryInfoSubtitle } from 'src/components/EntityPage/CommonSummaryInfo/CommonSummaryInfo';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useMetricFormatter } from 'src/utils/Hooks';
import { useProductEntity } from 'src/utils/Hooks/reduxSelectors';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { BEACON_INDEX_NAMES } from 'src/utils/entityDefinitions/indexes/beaconIndexNames';

/**
 * Promotions Summary Page under the Prices tab.
 */
const PromotionsSummaryPage = () => {
  const { isProductEntity } = useProductEntity();
  const formatMetric = useMetricFormatter();

  const renderView = useCallback(() => {
    if (isProductEntity) {
      return (
        <BasePageContainer>
          <Flex gap="lg" flexDirection="column">
            <CommonSummaryInfoSubtitle title="Promotion Count" />
            <PromotionMetricCards />
          </Flex>
          <Flex marginTop="80px" gap={BEACON_CHART_SPACING} flexDirection="column">
            <PromotionsDealTypeColumnChart />
            <PromotionsByWeekTable />
          </Flex>
        </BasePageContainer>
      );
    } else {
      return (
        <BasePageContainer>
          <Flex gap={BEACON_CHART_SPACING} flexDirection="column">
            <Flex gap="lg" flexDirection="column">
              <CommonSummaryInfoSubtitle title="Promotion Count" />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <PromotionCountDonutChart />
                <div
                  style={{
                    width: '624px'
                  }}
                >
                  <BeaconComparableSplineChart
                    aggregationFunctionForTotal="sum"
                    beaconChartWithLegendOverride={(
                      props,
                      { primaryTotal, secondaryTotal, formattedPercentChange }
                    ) => {
                      return {
                        ...props,
                        primaryLegendProps: {
                          ...props.primaryLegendProps,
                          metric: formatMetric(primaryTotal, METRICTYPE.VOLUME),
                          change: formattedPercentChange
                        },
                        comparisonLegendProps: {
                          ...props.comparisonLegendProps,
                          metric: formatMetric(secondaryTotal, METRICTYPE.VOLUME)
                        }
                      };
                    }}
                    indexName={BEACON_INDEX_NAMES.PROMOTIONS}
                    fieldName="stacklineSku"
                    showTitleSummary={false}
                    splineChartLoadingProps={{
                      width: 543
                    }}
                    chartOptionsOverride={{
                      chart: {
                        height: 300
                      }
                    }}
                    requestBuilder={(builder) => {
                      builder.addConditionRangeFilter('retailSales', 1);
                      return builder;
                    }}
                  />
                </div>
              </div>
            </Flex>
            <PromotionsTopEntityChart />
            <PromotionsDealTypeColumnChart />
            <PromotionsProductTable />
          </Flex>
        </BasePageContainer>
      );
    }
  }, [isProductEntity, formatMetric]);

  return renderView();
};

export default PromotionsSummaryPage;
